import { useEffect, useMemo, useState } from 'react'
import { NewFolderPlus } from '@/assets/icons'
import { MatterSelectDropdown } from '@/components/matter-select'
import { Modal, ModalFooterWithActions, TabbedModal } from '@/components/modal'
import { selectMatterFilesMemberMattersSortedByName, selectMattersSortedByName } from '@/matter/store/selectors'
import { canMoveMatterFilesExternally } from '@/matter/utils/matter-permissions'
import { isUnassignedMatter } from '@/matter/utils/is-unassigned-matter'
import { useAppSelector } from '@/store/store-hooks'
import { selectFoldersRecord } from '@/user_files/folders/store/selectors'
import { getFolderAncestorIds } from '@/user_files/folders/utils/get-folder-ancestor-ids'
import { BrowseMatterFiles } from '@/user_files/components/BrowseMatterFiles'
import { useBrowseFiles } from '@/user_files/components/useBrowseFiles'
import { kGetFeatureFlagStatus, kFeatureFlags } from '@/constants/constants-feature-flags'
import { filterLocalStorageItems, isLocalStorageItem } from '@/user_files/external-storage/utils/external-storage-items'
import { CreateFolderModal } from './CreateFolderModal'

const MOVE_TAB_MATTER_FILES = 'matter-files'
const MOVE_TAB_TEMPLATES = 'templates'

export type MoveItemsDialogProps = {
  open: boolean
  initialMatterId: string
  initialFolderId: string
  fileIds: string[]
  folderIds: string[]
  submitting: boolean
  error: string | null
  onClose: () => void
  onConfirm: (destinationFolderId: string) => void
}

function getMoveTitle(params: { fileCount: number; folderCount: number }): string {
  const { fileCount, folderCount } = params
  const total = fileCount + folderCount

  if (total === 1) {
    if (fileCount === 1) return 'Move 1 file to…'
    if (folderCount === 1) return 'Move 1 folder to…'
    return 'Move 1 item to…'
  }

  if (fileCount > 0 && folderCount === 0) return `Move ${fileCount} files to…`
  if (folderCount > 0 && fileCount === 0) return `Move ${folderCount} folders to…`
  return `Move ${total} items to…`
}

export function MoveItemsDialog({ error, fileIds, folderIds, initialFolderId, initialMatterId, onClose, onConfirm, open, submitting }: MoveItemsDialogProps) {
  const matters = useAppSelector(selectMatterFilesMemberMattersSortedByName)
  const allMatters = useAppSelector(selectMattersSortedByName)
  const foldersById = useAppSelector(selectFoldersRecord)

  const fileCount = fileIds.length
  const folderCount = folderIds.length
  const totalSelectedItems = fileCount + folderCount

  const [selectedMatterId, setSelectedMatterId] = useState<string>(initialMatterId)
  const [localError, setLocalError] = useState<string | null>(null)
  const [isCreateFolderOpen, setIsCreateFolderOpen] = useState(false)
  const [activeTabId, setActiveTabId] = useState<string>(MOVE_TAB_MATTER_FILES)

  const initialFolder = foldersById[initialFolderId]?.data
  const initialFolderIsExternalStorage = initialFolder ? !isLocalStorageItem(initialFolder) : false
  const initialBrowseFolderId = initialFolderIsExternalStorage ? undefined : initialFolderId

  const {
    breadcrumbItems,
    folderId: browseFolderId,
    folders: browseFolders,
    files: browseFiles,
    loading,
    error: loadingError,
    onNavigateToFolder,
    retry,
  } = useBrowseFiles({
    matterId: selectedMatterId,
    open,
    initialFolderId: initialBrowseFolderId,
    disabled: submitting,
    onNavigate: () => setLocalError(null),
  })

  // exclude DMS folders as a move target, as we don't currently support uploading files to DMS
  const folders = useMemo(() => filterLocalStorageItems(browseFolders), [browseFolders])
  const files = useMemo(() => filterLocalStorageItems(browseFiles), [browseFiles])

  // browseFolderId is null while the hook resolves the initial folder; treat as unchanged until resolved.
  const isDestinationUnchanged = browseFolderId === null || browseFolderId === initialBrowseFolderId

  useEffect(() => {
    if (!open) return
    setSelectedMatterId(initialMatterId)
    setLocalError(null)
    setActiveTabId(MOVE_TAB_MATTER_FILES)
  }, [open, initialMatterId])

  const handleMatterChange = (nextMatterId: string) => {
    setSelectedMatterId(nextMatterId)
    setLocalError(null)
  }

  const initialMatter = allMatters.find((m) => m.id === initialMatterId)
  const canMove = isUnassignedMatter(initialMatter) || canMoveMatterFilesExternally(initialMatter)

  const destinationAncestorIds = useMemo(() => {
    if (!browseFolderId) return []
    return getFolderAncestorIds(foldersById, browseFolderId)
  }, [foldersById, browseFolderId])

  const handleMove = () => {
    if (!browseFolderId) return

    if (folderIds.length > 0 && destinationAncestorIds.some((id) => folderIds.includes(id))) {
      const isDirectSelf = folderIds.includes(browseFolderId)
      setLocalError(isDirectSelf ? "Can't move a folder into itself." : "Can't move a folder into its own subfolder.")
      return
    }

    setLocalError(null)
    onConfirm(browseFolderId)
  }

  if (!open || totalSelectedItems === 0) return null

  const title = getMoveTitle({ fileCount, folderCount })
  const inlineError = localError || error

  return (
    <Modal
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose()
      }}
    >
      <TabbedModal
        className="bg-white"
        title={title}
        width="lg"
        height="md"
        activeTabId={activeTabId}
        footer={
          <ModalFooterWithActions
            inlineMessage={inlineError ? { variant: 'error', children: inlineError } : undefined}
            tertiaryAction={{
              icon: <NewFolderPlus className="w-4 h-4" />,
              label: 'New Folder',
              disabled: activeTabId !== MOVE_TAB_MATTER_FILES || submitting,
              onClick: () => setIsCreateFolderOpen(true),
            }}
            secondaryAction={{ label: 'Cancel', onClick: onClose }}
            primaryAction={{
              label: 'Move',
              disabled: activeTabId !== MOVE_TAB_MATTER_FILES || isDestinationUnchanged || loading,
              onClick: handleMove,
              loading: submitting,
            }}
          />
        }
        onTabChange={setActiveTabId}
        tabBarTrailing={
          canMove ? (
            <MatterSelectDropdown
              aria-label="Change matter"
              arrowIcon="ChevronDown"
              className="z-[1001]"
              disabled={submitting}
              items={matters}
              onSelect={handleMatterChange}
              placeholder="Change Matter"
              selectedItemId={selectedMatterId}
              triggerLabel="Change Matter"
              variant="inline"
            />
          ) : undefined
        }
        tabs={[
          {
            id: MOVE_TAB_MATTER_FILES,
            label: 'Matter Files',
            panel: (
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                <BrowseMatterFiles
                  breadcrumbItems={breadcrumbItems}
                  disabled={submitting}
                  files={files}
                  folders={folders}
                  blockingError={loadingError}
                  loading={loading}
                  onNavigateToFolder={onNavigateToFolder}
                  onRetry={retry}
                />
              </div>
            ),
          },
          ...(kGetFeatureFlagStatus(kFeatureFlags.TEMPLATES_TAB)
            ? [
                {
                  id: MOVE_TAB_TEMPLATES,
                  label: 'Templates',
                  panel: (
                    <div className="flex min-h-[240px] flex-col items-center justify-center px-6 py-10 text-center text-sm text-brand-neutral-500">
                      <p>Templates are not available yet.</p>
                    </div>
                  ),
                },
              ]
            : []),
        ]}
      />
      {browseFolderId && (
        <CreateFolderModal
          open={isCreateFolderOpen}
          parentFolderId={browseFolderId}
          onClose={() => setIsCreateFolderOpen(false)}
          onFolderCreated={onNavigateToFolder}
        />
      )}
    </Modal>
  )
}
